
import { Component, Prop, Vue } from "vue-property-decorator";
import vIconWithTooltip from "@/components/vIconWithTooltip.vue";
import { round } from "@/utils/Helpers";
import { mapState } from "vuex";
import { RecipeBreakdown } from "@/models/Recipes/RecipeBreakdown";
import { RecipeIngredientBreakdown } from "@/models/Ingredient/RecipeIngredientBreakdown";
import { NutrientBreakdown } from "@/models/Recipes/RecipeBreakdown";
import { LossType } from "@/models/Recipes/LossType";

@Component({
  methods: { round },
  components: {
    vIconWithTooltip,
  },
  computed: {
    ...mapState({
      localizationState: (state: any) => state.LocalizationModule,
    }),
  },
})

export default class vRecipeBreakdownTable extends Vue {
  @Prop() recipesCalculation: RecipeBreakdown;
  @Prop() tableHeaders: string[];
  @Prop() slicedTableHeaders: string[];
  @Prop({ default: false }) showAllergenColumn: boolean;
  @Prop({ default: () => ({}) }) ingredientAllergensByName: Record<string, string>;
  @Prop() isAdmin: boolean;
  @Prop({ default: false }) useRoundedValues!: boolean;

  localizationState: any;
  showChildRows: boolean[] = [];

  getNutrientFacts(){
    return this.recipesCalculation.nutrientFacts;
  }

  getFlattenedIngredients() {
    return this.flattenIngredients(this.getNutrientFacts().ingredients);
  }

  getIngredientAllergenText(ingredient: RecipeIngredientBreakdown) {
    if (ingredient.allergenText) {
      return ingredient.allergenText;
    }

    const lookupKey = this.normalizeIngredientLookupKey(ingredient.ingredientName);
    return this.ingredientAllergensByName?.[lookupKey] || "--";
  }

  toggleChildRows(index: number): void {
    this.$set(this.showChildRows, index, !this.showChildRows[index]);
  }

  flattenIngredients(
    ingredients: RecipeIngredientBreakdown[] = [],
    level: number = 0,
    ancestorLastFlags: boolean[] = []
  ): Array<{ ingredient: RecipeIngredientBreakdown; level: number; isLast: boolean; ancestorLastFlags: boolean[]; moistureLossSummary: string }> {
    const rows: Array<{ ingredient: RecipeIngredientBreakdown; level: number; isLast: boolean; ancestorLastFlags: boolean[]; moistureLossSummary: string }> = [];

    for (let i = 0; i < ingredients.length; i++) {
      const ingredient = ingredients[i];
      const isLast = i === ingredients.length - 1;
      rows.push({
        ingredient,
        level,
        isLast,
        ancestorLastFlags: [...ancestorLastFlags],
        moistureLossSummary: this.getSubrecipeMoistureLossSummary(ingredient),
      });

      const childIngredients = ingredient.childRecipeBreakdown?.nutrientFacts?.ingredients || [];
      if (childIngredients.length) {
        rows.push(...this.flattenIngredients(childIngredients, level + 1, ancestorLastFlags.concat(isLast)));
      }
    }

    return rows;
  }

  getIngredientCellStyle(level: number) {
    return {
      paddingLeft: `${12 + (level * 34)}px`,
    };
  }

  // Build a tree-like hierarchy marker using ancestorLastFlags and isLast.
  // For intermediate ancestors: show "│  " when that ancestor has siblings after it, otherwise spaces.
  // For the current level: show "├-" when not last, and "└-" when last.
  getHierarchyMarker(row: { level: number; isLast: boolean; ancestorLastFlags: boolean[] }) {
    const { level, isLast, ancestorLastFlags } = row as any;
    if (level <= 0) {
      return "";
    }

    let marker = "";
    // For each ancestor (except the current level), draw vertical lines for non-last ancestors
    for (let i = 0; i < Math.max(0, level - 1); i++) {
      marker += ancestorLastFlags && ancestorLastFlags[i] ? "  " : "┃";
    }

    // Terminal marker for this level: ├- for non-last, └- for last
    marker += isLast ? "┗━" : "┣━";
    return marker + " ";
  }

  getNutrientValue(ingredient: RecipeIngredientBreakdown, nutrient: string) {
    const nutrientData = ingredient[nutrient as keyof RecipeIngredientBreakdown] || {
      per100Grams: { value: '-', unit: '' },
      inRecipe: { value: '-', unit: '' },
      inRecipeAfterLoss: { value: '-', unit: '' }
    };

    if(nutrient == "energy") {
      nutrientData.inRecipe.value = this.trialMaskedNutrientData(nutrientData.inRecipe.value);
      return nutrientData;
    }
    nutrientData.inRecipe.value = round(nutrientData.inRecipe.value, 2);
    return nutrientData;
  }

  getNutrientAbsoluteValue(nutrientKey: string) {
    const nutrient = this.recipesCalculation[nutrientKey];
    if (this.recipesCalculation[nutrientKey] != null) {
      if(nutrientKey == "energy"){
        nutrient.absoluteValue.value = this.trialMaskedNutrientData(nutrient.absoluteValue.value);
        return nutrient.absoluteValue;
      }
      if (this.recipesCalculation[nutrientKey].absoluteValue != null) {
        nutrient.absoluteValue.value = round(nutrient.absoluteValue.value, 2);
        return nutrient.absoluteValue;
      }
    }
    if (!nutrient) {
      return { value: 'N/A', unit: '' };
    }
  }

  getNutrientPerServingValue(nutrientKey: string) {
    const nutrient: NutrientBreakdown = this.recipesCalculation[nutrientKey];
    if (this.recipesCalculation[nutrientKey] != null) {
      if(nutrientKey == "energy"){
        nutrient.roundedValuePerServing.value = this.trialMaskedNutrientData(nutrient.roundedValuePerServing.value);
        nutrient.absoluteValuePerServing.value =  this.trialMaskedNutrientData(nutrient.absoluteValuePerServing.value);
        return this.useRoundedValues
          ? nutrient.roundedValuePerServing
          : nutrient.absoluteValuePerServing;
      }
      if (this.recipesCalculation[nutrientKey].absoluteValuePerServing != null) {
        nutrient.roundedValuePerServing.value = round(nutrient.roundedValuePerServing.value, 2);
        nutrient.absoluteValuePerServing.value = round(nutrient.absoluteValuePerServing.value, 2);
        return this.useRoundedValues
          ? nutrient.roundedValuePerServing
          : nutrient.absoluteValuePerServing;
      }
    }
    if (!nutrient) {
      return { value: 'N/A', unit: '' };
    }
  }

  trialMaskedNutrientData(value: number, shouldRound: boolean = true) {
    if (this.$store.getters.getUserDetails.isTrial) {
      return "--";
    }

    return shouldRound ? round(value, 2) : value;
  }

  overrideWeightDisplay(valueIfNotOverridden: number): string {
    return `${this.getNutrientFacts().overrideWeightPerServing || valueIfNotOverridden}${this.getNutrientFacts().overrideUnit || "g"}`;
  }

  normalizeIngredientLookupKey(value: string) {
    return (value || "").trim().toLowerCase();
  }

  getTranslation(key: string): string {
    return this.localizationState?.translations?.get(key) || key;
  }

  getSubrecipeMoistureLossSummary(ingredient: RecipeIngredientBreakdown) {
    const childRecipeBreakdown = ingredient?.childRecipeBreakdown;
    if (!childRecipeBreakdown) {
      return "";
    }

    const lossAmount = Number(childRecipeBreakdown.lossAmount || 0);
    const lossType = childRecipeBreakdown.lossType;

    if (lossAmount <= 0 || lossType == null) {
      return "";
    }

    const unit = lossType === LossType.Absolute ? "g" : "%";
    return this.getTranslation("rec_moisture_loss_applied")
      .replace("{0}", `${round(lossAmount, 2)}${unit}`);
  }
};
    